import React, { useState, useRef } from 'react';
import { Button } from 'reactstrap';
import ReactCrop, {
  centerCrop,
  convertToPixelCrop,
  makeAspectCrop,
} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { partial } from '../../../util/translation/translation';
import Slider from './Slider';
import FormModal from './FormModal';

const CROP_TYPES = {
  // No fixed shape: starts as the whole image, dragged to any size
  FREE_CROP: { width: 100, height: 100, x: 0, y: 0, unit: '%' },
  BANNER_CROP: {
    height: 1125,
    width: 630,
    x: 0,
    y: 0,
    aspect: 16 / 9,
    unit: 'px',
  },
  PROFILE_CROP: { width: 750, height: 750, x: 0, y: 0, aspect: 1, unit: 'px' },
  MATCHSPONSOR_CROP: {
    height: 900,
    width: 90,
    x: 0,
    y: 0,
    aspect: 10 / 1,
    unit: 'px',
  },
  PLAYERCARD_CROP: {
    height: 900,
    width: 90,
    x: 0,
    y: 0,
    aspect: 10 / 1,
    unit: 'px',
  },
  PRINTEDPAPERTICKET_CROP: {
    height: 800,
    width: 160,
    x: 0,
    y: 0,
    aspect: 10 / 1,
    unit: 'px',
  },
  FREE_FORM_CROP: { width: 75, height: 75, x: 0, y: 0, aspect: 1, unit: 'px' },
  APP_SPONSOR_CROP: {
    width: 1035,
    height: 255,
    x: 0,
    y: 0,
    aspect: 4 / 1,
    unit: 'px',
  },
  TIER_CROP: { width: 200, height: 200, x: 0, y: 0, aspect: 1, unit: 'px' },
  PLAYER_IMAGE_CROP: {
    width: 750,
    height: 750,
    x: 0,
    y: 0,
    aspect: 750 / 750,
    unit: 'px',
  },
  SPOTLIGHT_SQUARE_CROP: {
    width: 750,
    height: 750,
    x: 0,
    y: 0,
    aspect: 750 / 750,
    unit: 'px',
  },
  SPOTLIGHT_LANDSCAPE_CROP: {
    width: 1125,
    height: 630,
    x: 0,
    y: 0,
    aspect: 16 / 9,
    unit: 'px',
  },
  SPOTLIGHT_PORTRAIT_CROP: {
    width: 630,
    height: 1125,
    x: 0,
    y: 0,
    aspect: 9 / 16,
    unit: 'px',
  },
  // Story slides fill the phone screen (9:16), as the app shows them
  SPOTLIGHT_STORY_CROP: {
    width: 630,
    height: 1125,
    x: 0,
    y: 0,
    aspect: 9 / 16,
    unit: 'px',
  },
  SPOTLIGHT_BANNER_CROP: {
    width: 750,
    height: 211,
    x: 0,
    y: 0,
    aspect: 16 / 4.5,
    unit: 'px',
  },
  SPOTLIGHT_BANNER_SMALL_CROP: {
    width: 375,
    height: 40,
    x: 0,
    y: 0,
    aspect: 75 / 8,
    unit: 'px',
  },
  SPOTLIGHT_BANNER_MEDIUM_CROP: {
    width: 375,
    height: 80,
    x: 0,
    y: 0,
    aspect: 75 / 16,
    unit: 'px',
  },
  SPOTLIGHT_BANNER_LARGE_CROP: {
    width: 375,
    height: 150,
    x: 0,
    y: 0,
    aspect: 5 / 2,
    unit: 'px',
  },
  SPOTLIGHT_CIRCLE_CROP: {
    width: 750,
    height: 750,
    x: 0,
    y: 0,
    aspect: 750 / 750,
    unit: 'px',
  },
  SPOTLIGHT_HALF_PORTRAIT_CROP: {
    width: 800,
    height: 1000,
    x: 0,
    y: 0,
    aspect: 4 / 5,
    unit: 'px',
  },
  NEWS_THUMB_CROP: {
    height: 1035,
    width: 585,
    x: 0,
    y: 0,
    aspect: 16 / 9,
    unit: 'px',
  },
  NEWS_THUMB_SHORT_CROP: {
    width: 630,
    height: 1125,
    x: 0,
    y: 0,
    aspect: 9 / 16,
    unit: 'px',
  },
  MATCHBANNER_IMAGE_CROP: {
    width: 500,
    height: 500,
    x: 0,
    y: 0,
    aspect: 500 / 500,
    unit: 'px',
  },
  // News image blocks: the app shows them full width in their own
  // proportions, so any of these shapes works (a carousel takes the first
  // image's shape for all its images)
  NEWS_OPTIONS_CROP: {
    height: 1035,
    width: 585,
    x: 0,
    y: 0,
    aspect: 16 / 9,
    unit: 'px',
    shapes: ['LANDSCAPE', 'SQUARE', 'PORTRAIT'],
  },
  SQUARE: { height: 750, width: 750, x: 0, y: 0, aspect: 1 / 1, unit: 'px' },
  LANDSCAPE: {
    height: 1280,
    width: 720,
    x: 0,
    y: 0,
    aspect: 16 / 9,
    unit: 'px',
  },
};

// Shapes to choose between, for images the app shows in their own
// proportions (a crop type's `shapes`, or a field's own `shapes`)
const SHAPES = {
  LANDSCAPE: { aspect: 16 / 9, ratio: '16:9' },
  SQUARE: { aspect: 1, ratio: '1:1' },
  PORTRAIT: { aspect: 4 / 5, ratio: '4:5' },
  WIDE: { aspect: 4, ratio: '4:1' },
  FREE: { aspect: undefined, ratio: '' },
};

// The shape's outline in a 22px box, for its chip
const shapeIconStyle = (aspect) => {
  if (!aspect) return { width: 18, height: 18 };
  return aspect >= 1
    ? { width: 22, height: Math.max(22 / aspect, 5) }
    : { width: 22 * aspect, height: 22 };
};

// The largest box of the shape that fits the image, centred (the whole
// image when the shape is free), in % of the image
const startCrop = (width, height, aspect) =>
  aspect
    ? centerCrop(
        makeAspectCrop(
          width / height > aspect
            ? { unit: '%', height: 100 }
            : { unit: '%', width: 100 },
          aspect,
          width,
          height
        ),
        width,
        height
      )
    : { unit: '%', x: 0, y: 0, width: 100, height: 100 };

// A saved crop is reused only while it still has the shape's proportions
const hasAspect = (pixelCrop, aspect) =>
  !aspect ||
  Math.abs(pixelCrop.width / pixelCrop.height - aspect) / aspect < 0.02;

const VIEW_TYPE = {
  NEW: 'new',
  EDIT: 'edit',
};
const s = partial('shared');
const p = partial('ImageCrop');
const ImageCropModal = (props) => {
  const cropType = CROP_TYPES[props.crop] || CROP_TYPES.PROFILE_CROP;
  const shapes = (props.shapes || cropType.shapes || []).filter(
    (key) => SHAPES[key]
  );
  // With shapes to choose from, the chosen one sets the proportions
  const [shape, setShape] = useState(shapes[0]);
  const aspect = shape ? SHAPES[shape].aspect : cropType.aspect;
  const [crop, setCrop] = useState(cropType);
  const [percentageCrop, setPercentageCrop] = useState({
    width: 100,
    height: 100,
    x: 0,
    y: 0,
    unit: '%',
  });
  const [croppedImage, setCroppedImage] = useState(null);
  const imageRef = useRef(null);
  // Editing again starts from the scale chosen last time
  const [scale, setScale] = useState(props.initialScale || 1);

  const getCroppedImg = (image, croped, fileName, withScale) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const pixelRatio = window.devicePixelRatio;

    canvas.width = Math.floor(croped.width * scaleX * pixelRatio);
    canvas.height = Math.floor(croped.height * scaleY * pixelRatio);

    ctx.scale(pixelRatio, pixelRatio);
    ctx.imageSmoothingQuality = 'high';

    const cropX = croped.x * scaleX;
    const cropY = croped.y * scaleY;

    const centerX = image.naturalWidth / 2;
    const centerY = image.naturalHeight / 2;

    ctx.save();

    ctx.translate(-cropX, -cropY);
    ctx.translate(centerX, centerY);
    ctx.scale(withScale, withScale);
    ctx.translate(-centerX, -centerY);
    ctx.drawImage(
      image,
      0,
      0,
      image.naturalWidth,
      image.naturalHeight,
      0,
      0,
      image.naturalWidth,
      image.naturalHeight
    );

    ctx.restore();
    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          blob.name = fileName;
          window.URL.revokeObjectURL(blob.fileUrl);
          blob.fileUrl = window.URL.createObjectURL(blob);
          resolve(blob);
        },
        'image/png',
        0.25
      );
    });
  };

  // The scale is passed in: right after a change the state still has the
  // previous one
  const makeClientCrop = async (croped, withScale = scale) => {
    if (imageRef.current && croped.width && croped.height) {
      const croppedImageResult = await getCroppedImg(
        imageRef.current,
        croped,
        'newFile.png',
        withScale
      );
      setCroppedImage(croppedImageResult);
    }
  };

  // Shows a crop given in % (what is saved) on the image as it's shown
  const applyCrop = (percent, width, height) => {
    const pixel = convertToPixelCrop(percent, width, height);
    setCrop(pixel);
    setPercentageCrop(percent);
    makeClientCrop(pixel);
  };

  const onCropComplete = (croped) => {
    makeClientCrop(croped);
  };

  const onImageLoaded = (e) => {
    const { width, height } = e.currentTarget;
    // Editing again: the crop saved last time (in %), when there is one
    const saved = props.initialCrop;
    if (saved?.unit === '%' && saved.width > 0 && saved.height > 0) {
      const savedPixels = convertToPixelCrop(saved, width, height);
      if (shapes.length) {
        // The shape it was cropped in: a fixed one it matches, else free
        const savedShape =
          shapes.find(
            (key) =>
              SHAPES[key].aspect && hasAspect(savedPixels, SHAPES[key].aspect)
          ) || shapes.find((key) => !SHAPES[key].aspect);
        if (savedShape) {
          setShape(savedShape);
          applyCrop(saved, width, height);
          return;
        }
      } else if (hasAspect(savedPixels, aspect)) {
        applyCrop(saved, width, height);
        return;
      }
    }
    applyCrop(startCrop(width, height, aspect), width, height);
  };

  const onScaleChange = (newScale) => {
    setScale(newScale);
    makeClientCrop(crop, newScale);
  };

  const getHeaderMessage = () => {
    switch (props.crop) {
      case 'BANNER_CROP':
        return p('headerMsgCover');
      case 'PROFILE_CROP':
        return p('headerMsgProfile');
      case 'MATCHSPONSOR_CROP':
        return p('headerMsgMatchSponsor');
      case 'PLAYERCARD_CROP':
        return p('headerMsgPlayerCard');
      case 'PLAYER_IMAGE_CROP':
        return p('cropImageToASquare');
      case 'PRINTEDPAPERTICKET_CROP':
        return p('headerMsgTicket');
      case 'NEWS_THUMB_CROP':
        return p('newsThumbnailCrop');
      case 'NEWS_THUMB_SHORT_CROP':
        return p('newsThumbnailShortCrop');

      case 'NEWS_OPTIONS_CROP':
        return p('newsOptionsCrop');
      default:
        return '';
    }
  };
  const getSubMessage = () => {
    switch (props.crop) {
      case 'PLAYER_IMAGE_CROP':
        return p('playerCropDescription');
      default:
        return '';
    }
  };
  async function getFileFromUrl(url, name, defaultType = 'image/jpeg') {
    const response = await fetch(url);
    const data = await response.blob();
    return new File([data], name, {
      type: data.type || defaultType,
    });
  }
  const handleSubmit = async () => {
    if (croppedImage) {
      const originalFile = await getFileFromUrl(props.src, 'test');
      originalFile.preview = props.src;
      originalFile.crop = percentageCrop;
      originalFile.scale = scale;
      const croppedFile = new File([croppedImage], 'newFile.png');
      croppedFile.preview = croppedImage.fileUrl;
      croppedFile.crop = percentageCrop;
      croppedFile.scale = scale;
      props.action([croppedFile, originalFile]);
      props.handleModal(false);
    } else {
      // Nothing cropped yet (the image hasn't loaded): same as cancelling
      props.handleModal(false);
    }
  };

  // Another shape starts from the largest box of that shape
  const selectShape = (key) => {
    setShape(key);
    const image = imageRef.current;
    if (image) {
      applyCrop(
        startCrop(image.width, image.height, SHAPES[key].aspect),
        image.width,
        image.height
      );
    }
  };

  const headerMessage = getHeaderMessage();
  const subMessage = getSubMessage();
  const cancel = () => props.handleModal(false);
  const scalePercent = (value) => `${Math.round(value * 100)} %`;

  return (
    <FormModal
      isOpen={props.visible}
      onClose={cancel}
      title={p('title')}
      // The recommended size when there is one (news images choose a shape
      // below instead)
      subtitle={
        headerMessage && props.crop !== 'NEWS_OPTIONS_CROP'
          ? headerMessage
          : p('headerTitle')
      }
      size="lg"
      className="crop-modal"
    >
      <div className="qm-body">
        <div className="crop-modal__layout">
          <div className="crop-modal__stage">
            <ReactCrop
              className={`crop-modal__cropper${
                props.crop === 'PLAYERCARD_CROP'
                  ? ' crop-modal__cropper--dark'
                  : ''
              }`}
              crop={crop}
              onChange={(pixel, percentage) => {
                setCrop(pixel);
                setPercentageCrop(percentage);
              }}
              onComplete={onCropComplete}
              aspect={aspect}
              locked={props.locked}
              unit={cropType.unit}
            >
              <img
                style={{ transform: `scale(${scale})` }}
                // An image already uploaded comes from another address
                crossOrigin={
                  props.type === VIEW_TYPE.EDIT ? 'anonymous' : undefined
                }
                ref={imageRef}
                src={props.src}
                alt={p('title')}
                onLoad={onImageLoaded}
              />
            </ReactCrop>
            {headerMessage && props.crop !== 'NEWS_OPTIONS_CROP' && (
              <span className="qm-muted">{p('headerTitle')}</span>
            )}
          </div>

          <div className="crop-modal__side">
            {shapes.length > 1 && (
              <div className="qm-section">
                <span className="qm-label">{p('shape')}</span>
                <div
                  className="crop-shapes"
                  role="radiogroup"
                  aria-label={p('shape')}
                >
                  {shapes.map((key) => (
                    <button
                      key={key}
                      type="button"
                      role="radio"
                      aria-checked={shape === key}
                      className={`crop-shape${
                        shape === key ? ' crop-shape--on' : ''
                      }`}
                      onClick={() => selectShape(key)}
                    >
                      <span className="crop-shape__box" aria-hidden>
                        <span
                          className={`crop-shape__icon${
                            SHAPES[key].aspect ? '' : ' crop-shape__icon--free'
                          }`}
                          style={shapeIconStyle(SHAPES[key].aspect)}
                        />
                      </span>
                      {p(`shape-${key}`)}
                      {SHAPES[key].ratio && (
                        <span className="crop-shape__ratio">
                          {SHAPES[key].ratio}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="qm-section">
              <span className="qm-label">
                {p('scaleDown')}: {scalePercent(scale)}
              </span>
              <Slider
                name="scale-input"
                step={0.1}
                setValue={onScaleChange}
                value={scale}
                min={0.1}
                max={1}
                tipFormatter={scalePercent}
              />
            </div>

            {subMessage && <p className="qm-muted mb-0">{subMessage}</p>}

            <div className="qm-section">
              <span className="qm-label">{s('previewImage')}</span>
              {croppedImage && props.crop === 'PLAYERCARD_CROP' && (
                <div className="sponsor-player bg-dark">
                  <img src={croppedImage.fileUrl} alt="" />
                </div>
              )}
              {croppedImage && props.crop === 'PROFILE_CROP' && (
                <img
                  className="company-logo"
                  src={croppedImage.fileUrl}
                  alt=""
                />
              )}
              {croppedImage &&
                props.crop !== 'PLAYERCARD_CROP' &&
                props.crop !== 'PROFILE_CROP' && (
                  // On a checkerboard, so space left by scaling down shows
                  <div className="crop-modal__preview">
                    <img src={croppedImage.fileUrl} alt="" />
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
      <div className="qm-footer">
        <Button
          type="button"
          outline
          color="secondary"
          className="mb-0"
          onClick={cancel}
        >
          {p('cancel')}
        </Button>
        <Button
          type="button"
          color="primary"
          className="mb-0"
          disabled={!croppedImage}
          onClick={handleSubmit}
        >
          {s('continue')}
        </Button>
      </div>
    </FormModal>
  );
};

export default ImageCropModal;
